import {
  createApi,
  BaseQueryFn,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { RootState } from "app/store";
import { getAuthToken } from "features/auth/utils";
import { withReauth } from "services/shared/withReauth";

const rawDynamicBaseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  // Check if this request should be delayed
  const delay = (extraOptions as any)?.delay;
  if (delay) {
    await new Promise((resolve) => setTimeout(resolve, delay));
  }

  const { mainApiUrl } = (api.getState() as RootState).settings.endpoints;
  const rawBaseQuery = fetchBaseQuery({
    baseUrl: mainApiUrl,
    prepareHeaders: async (headers) => {
      const token =
        (extraOptions as any)?.tokenOverride ?? (await getAuthToken());
      if (token) {
        headers.set("authorization", token);
      }
      return headers;
    },
  });
  return rawBaseQuery(args, api, extraOptions);
};

/**
 * Wraps rawDynamicBaseQuery with automatic token refresh on 401.
 * On 401: attempts a single token refresh (shared via singleton lock).
 * If refresh succeeds, retries the request. If it fails, sets sessionExpired
 * in Redux and short-circuits all future requests.
 */
export const dynamicBaseQuery = withReauth(rawDynamicBaseQuery);

export const mainApi = createApi({
  reducerPath: "mainApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "Documents",
    "AtlasDocuments",
    "Escalation",
    "ActivityLogs",
    "AuditDetail",
    "Filters",
    "GaiaObjectDetail",
    "KBApprovals",
    "Approvals",
    "Tasks",
    "TaskList",
    "TaxArea",
    "Teams",
    "AssignmentGroups",
    "EscalationTemplate",
    "AgentRunHistory",
    "AgentActionReviews",
  ],
  endpoints: () => ({}),
});
